import { TFunction, useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  CardList,
  ContentTemplate,
  SearchBar,
  SectionHeader,
  TitleHeader,
  Typography,
} from 'web-library';
import React, { useEffect, useMemo, useState } from 'react';
import { Theme as AugmentedTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import { AnnouncementDetail } from 'web-library/models/Announcements';
import EmptyIcon from 'web-library/stories/assets/images/empty_component.svg';
import { useMediaQuery, useTheme } from '@mui/material';
import { MutationStatus } from 'react-query';
import { usePostSearchAnnouncements } from '../../api-refactored/announcements/usePostSearchAnnouncements';
import AnnouncementCardDetail from './AnnouncementCardDetail';

const useStyles = makeStyles()((theme) =>
({
  noticeContainer: {
    marginBottom: theme.spacing(7),
  },
  sectionHeaderContainer: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  pill: {
    marginTop: theme.spacing(0.5),
    background: theme.color.pill.pillInfoBg,
    color: theme.color.pill.pillInfoText,
    border: 'none',
    '& .MuiTypography-root': {
      color: theme.color.pill.pillInfoText,
    },
  },
  emptyResultContainer: {
    marginTop: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  resultContainer: {
    flexDirection: 'column',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      gap: '0px',
    },
  },
  card: {
    width: '100%',
    borderRadius: '0',
    background: theme.color.background.bgAlt,
  },
  divider: {
    height: '1px',
    padding: '0px',
  },
}));

interface RenderAnnouncementListingProps {
  data: AnnouncementDetail[];
  columns: { Header: string; accessor: string }[];
  renderAnnouncementDetail: (row: any) => JSX.Element;
  t: TFunction;
  loadMore: () => void;
  pageNumber: number;
  announcementDetailStatus: MutationStatus;
  isLastPage: boolean;
  classes: Record<string,string>;
}

const RenderAnnouncementListing = ({
  data,
  columns,
  renderAnnouncementDetail,
  t,
  loadMore,
  pageNumber,
  announcementDetailStatus,
  isLastPage,
  classes,
}: RenderAnnouncementListingProps) =>
  data.length ? (
    <CardList
      data={data}
      columns={columns}
      options={{
        customCardRender: renderAnnouncementDetail,
        loadMoreLabel: t('announcementsNotifications->>InformationPanel.LoadMoreNotices'),
        onLoadMore: loadMore,
        loadMoreStatus: pageNumber > 0 ? announcementDetailStatus : 'idle',
        isLastPage,
      }}
      cardListContainerClassName={classes.resultContainer}
      cardClassName={classes.card}
      isDisplayDivider
      dividerClassName={classes.divider}
    />
  ) : (
    <Box className={classes.emptyResultContainer}>
      <img alt="empty" src={EmptyIcon} />
      <Typography variant="h3">
        {t('announcementsNotifications->>InformationPanel.EmptyAnnouncement')}
      </Typography>
    </Box>
  );

const AnnouncementListing = () => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      { Header: 'Uuid', accessor: 'uuid' },
      { Header: 'Title', accessor: 'title' },
      { Header: 'Message', accessor: 'message' },
    ],
    []
  );

  const [isLastPage, setIsLastPage] = useState<boolean>(false);

  const [pageNumber, setPageNumber] = useState<number>(0);

  const [searchLoad, setSearchLoad] = useState<'search' | 'load'>('search');

  const [searchQuery, setSearchQuery] = useState<string>('');

  const theme = useTheme();

  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));

  const loadMore = () => {
    setSearchLoad('load');
    setPageNumber(pageNumber + 1);
  };

  const search = (searchStr: string) => {
    setSearchLoad('search');
    setSearchQuery(searchStr);
    setPageNumber(0);
  };

  const {
    data: announcementDetailPage,
    mutate: postSearchAnnouncements,
    status: announcementDetailStatus,
  } = usePostSearchAnnouncements({
    onSuccess: (res) => {
      if (searchLoad === 'search') {
        setData(res.content);
      } else {
        const tmpData = data.slice();
        tmpData.push(...res.content);
        setData(tmpData);
      }
      setIsLastPage(res.last);
    },
  });

  const [data, setData] = useState<AnnouncementDetail[]>(
    (announcementDetailPage && announcementDetailPage.content) || []
  );

  const { classes } = useStyles();

  useEffect(() => {
    postSearchAnnouncements({
      apiPathParam: {
        page: pageNumber,
        pageSize: 4,
      },
      announcementType: `ANNOUNCEMENTS`,
      q: searchQuery,
    });
  }, [pageNumber, postSearchAnnouncements, searchQuery]);

  const renderAnnouncementDetail = (row: any) => (
    <AnnouncementCardDetail announcementNoticeDetails={row.original} />
  );

  return (
    <>
      <TitleHeader title={t('announcementsNotifications->>InformationPanel.AllNotices')} />
      <ContentTemplate type="full">
        <Box className={classes.noticeContainer}>
          <Box className={classes.sectionHeaderContainer}>
            <SectionHeader title={t('announcementsNotifications->>InformationPanel.AllNotices')} />
            <Avatar className={classes.pill} type="text">
              {announcementDetailPage && announcementDetailPage.totalElements}
            </Avatar>
          </Box>
          <SearchBar
            placeholder={t('announcementsNotifications->>InformationPanel.SearchNotice')}
            name="searchNotice"
            ariaLabel="searchNotice"
            onSearch={(searchStr: string) => {
              search(searchStr);
            }}
            searchButton={!isMobileView}
          />
          <RenderAnnouncementListing
            announcementDetailStatus={announcementDetailStatus}
            classes={classes}
            columns={columns}
            data={data}
            isLastPage={isLastPage}
            loadMore={loadMore}
            pageNumber={pageNumber}
            renderAnnouncementDetail={renderAnnouncementDetail}
            t={t}
          />
        </Box>
      </ContentTemplate>
    </>
  );
};

export default AnnouncementListing;
